import { useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { GitPullRequestArrow, Ban, FileDiff, Copy, Check } from "lucide-react";
import { DelegationsApi } from "../lib/api";
import { qk } from "../lib/query";
import { useToast } from "../lib/toast";
import { ListDetail } from "../components/ListDetail";
import { Button, Pill, EmptyState, CenteredSpinner } from "../components/primitives";
import { PageHeader } from "../components/PageScaffold";
import { relativeTime, formatDuration, formatBytes } from "../lib/format";
import { cn } from "../lib/cn";
import type { DelegationMeta } from "../lib/types";

const STATUSES = ["running", "completed", "failed", "killed"] as const;
const AGENTS = ["reasonix", "kimi", "gemini", "claude", "codex"] as const;

export default function Delegations() {
  const qc = useQueryClient();
  const [agent, setAgent] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: rows = [], isLoading, error } = useQuery({
    queryKey: qk.delegations.list({ agent, status }),
    queryFn: () => DelegationsApi.list({ agent: agent || undefined, status: status || undefined, limit: 500 }),
    // Poll while anything is running so freshly-finished diffs land quickly.
    refetchInterval: (q) => (q.state.data?.some((d) => d.status === "running") ? 4000 : 15_000),
  });

  const runningCount = rows.filter((d) => d.status === "running").length;
  const failedCount = rows.filter((d) => d.status === "failed" || d.status === "killed").length;

  return (
    <div className="h-full flex flex-col min-h-0">
      <PageHeader
        title="Delegations"
        icon={GitPullRequestArrow}
        subtitle={
          rows.length
            ? `${rows.length} recent · ${runningCount} running · ${failedCount} failed`
            : "coding tasks offloaded to ACP workers in isolated worktrees"
        }
      />
      <div className="flex-1 min-h-0 panel mx-5 mb-5 overflow-hidden flex">
        <ListDetail<DelegationMeta>
          items={rows}
          itemKey={(d) => d.id}
          selectedId={selectedId}
          onSelect={setSelectedId}
          loading={isLoading}
          error={error as Error | null}
          itemHeight={72}
          toolbar={
            <div className="flex items-center gap-2 px-3 py-2">
              <select value={agent} onChange={(e) => setAgent(e.target.value)} className="h-7 text-xs w-32">
                <option value="">All agents</option>
                {AGENTS.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-7 text-xs w-32">
                <option value="">All statuses</option>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <span className="ml-auto text-[11px] text-fg-subtle font-mono">{rows.length}</span>
            </div>
          }
          emptyState={
            <EmptyState
              icon={<GitPullRequestArrow className="size-8" />}
              title="No delegations yet"
              description="Agents offload coding here via the delegate MCP tool."
            />
          }
          renderItem={({ item }) => <DelegationRow d={item} />}
          renderDetail={(d) => <DelegationDetail id={d.id} onKilled={() => qc.invalidateQueries({ queryKey: ["delegations"] })} />}
        />
      </div>
    </div>
  );
}

function statusTone(s: string): "neutral" | "accent" | "success" | "warning" | "danger" {
  switch (s) {
    case "running": return "accent";
    case "completed": return "success";
    case "failed": return "danger";
    case "killed": return "warning";
    default: return "neutral";
  }
}

function diffStatLabel(d: DelegationMeta): string | null {
  if (!d.diff_stat) return null;
  const { files, insertions, deletions } = d.diff_stat;
  if (files === 0) return "no changes";
  return `${files} file${files === 1 ? "" : "s"} · +${insertions} −${deletions}`;
}

function DelegationRow({ d }: { d: DelegationMeta }) {
  const isRunning = d.status === "running";
  const dur = d.ended_at
    ? formatDuration(new Date(d.ended_at).getTime() - new Date(d.started_at).getTime())
    : isRunning
      ? formatDuration(Date.now() - new Date(d.started_at).getTime()) + "…"
      : "—";
  const stat = diffStatLabel(d);
  return (
    <div className="px-3 py-2.5">
      <div className="flex items-center gap-2 mb-1">
        <span
          className={cn(
            "size-1.5 rounded-full flex-shrink-0",
            d.status === "running" && "bg-accent animate-pulse-dot",
            d.status === "completed" && "bg-success",
            d.status === "failed" && "bg-danger",
            d.status === "killed" && "bg-warning",
          )}
        />
        <span className="text-sm font-medium text-fg truncate">{d.task}</span>
        <span className="ml-auto text-[11px] text-fg-subtle font-mono whitespace-nowrap">
          {relativeTime(d.started_at).replace(" ago", "")}
        </span>
      </div>
      <div className="flex items-center gap-2 text-xs text-fg-muted">
        <Pill tone="info">{d.agent}</Pill>
        {stat && <span className="font-mono text-fg-subtle">{stat}</span>}
        <span className="ml-auto text-fg-subtle">{dur}</span>
        <Pill tone={statusTone(d.status)}>{d.status}</Pill>
      </div>
    </div>
  );
}

function DelegationDetail({ id, onKilled }: { id: string; onKilled: () => void }) {
  const toast = useToast();
  const { data, isLoading, error } = useQuery({
    queryKey: qk.delegations.detail(id),
    queryFn: () => DelegationsApi.detail(id),
    refetchInterval: (q) => (q.state.data?.meta.status === "running" ? 3000 : false),
  });
  const kill = useMutation({
    mutationFn: () => DelegationsApi.kill(id),
    onSuccess: (res) => {
      toast.success(res.already_dead ? "Worker already exited" : "Kill signal sent");
      onKilled();
    },
    onError: (err) => toast.error("Kill failed", String(err)),
  });

  if (isLoading) return <CenteredSpinner />;
  if (error) return <div className="p-5"><Pill tone="danger">Failed to load: {String(error)}</Pill></div>;
  if (!data) return null;
  const { meta, log, diff } = data;
  const isRunning = meta.status === "running";
  const dur = meta.ended_at
    ? formatDuration(new Date(meta.ended_at).getTime() - new Date(meta.started_at).getTime())
    : "running…";

  return (
    <div className="flex flex-col h-full">
      <div className="px-5 pt-4 pb-3 border-b border-border">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Pill tone="info">{meta.agent}</Pill>
              <Pill tone={statusTone(meta.status)}>{meta.status}</Pill>
              {meta.stop_reason && <Pill tone="neutral">{meta.stop_reason}</Pill>}
              {diffStatLabel(meta) && <span className="text-xs font-mono text-fg-subtle">{diffStatLabel(meta)}</span>}
            </div>
            <h2 className="text-sm font-semibold text-fg mt-2 leading-snug">{meta.task}</h2>
            <div className="text-[11px] text-fg-subtle mt-1.5 flex items-center gap-3 flex-wrap font-mono">
              <span className="truncate max-w-[20rem]" title={meta.repo}>{meta.repo}</span>
              {meta.requested_by && <span>by {meta.requested_by}</span>}
              <span>started {relativeTime(meta.started_at)}</span>
              <span>{dur}</span>
              {meta.pid && <span>pid {meta.pid}</span>}
              {meta.total_size !== undefined && <span>{formatBytes(meta.total_size)}</span>}
            </div>
          </div>
          {isRunning && (
            <Button
              size="sm"
              variant="danger"
              onClick={() => { if (confirm(`Kill ${meta.agent} delegation${meta.pid ? ` (pid ${meta.pid})` : ""}?`)) kill.mutate(); }}
              loading={kill.isPending}
              title={meta.pid ? "Send SIGTERM to the worker process" : "No pid recorded — clears the stale record if the worker is gone"}
            >
              <Ban className="size-3.5" />
              {meta.pid ? "Kill" : "Clear"}
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {meta.error && (
          <div className="panel border-danger/40 px-3 py-2 text-xs text-danger font-mono whitespace-pre-wrap">{meta.error}</div>
        )}
        {meta.summary && <Section label="Summary" content={meta.summary} />}
        {meta.tool_calls && meta.tool_calls.length > 0 && (
          <div>
            <SectionLabel>Tool calls · {meta.tool_calls.length}</SectionLabel>
            <div className="flex flex-wrap gap-1.5">
              {meta.tool_calls.map((t, i) => (
                <span key={i} className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-surface-2 border border-border text-fg-muted">{t}</span>
              ))}
            </div>
          </div>
        )}
        <DiffBlock diff={diff} running={isRunning} />
        {log && <Section label={`Worker log · ${log.length.toLocaleString()} chars`} content={log} mono collapsible />}
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <div className="text-[10px] uppercase tracking-wider font-semibold text-fg-subtle mb-1.5">{children}</div>;
}

function Section({ label, content, mono, collapsible }: { label: string; content: string; mono?: boolean; collapsible?: boolean }) {
  const [open, setOpen] = useState(!collapsible);
  return (
    <div>
      <button
        className={cn("text-[10px] uppercase tracking-wider font-semibold text-fg-subtle mb-1.5", collapsible && "hover:text-fg")}
        onClick={() => collapsible && setOpen((o) => !o)}
      >
        {label}{collapsible ? (open ? " ▾" : " ▸") : ""}
      </button>
      {open && (
        <pre className={cn("panel px-3 py-2 text-xs whitespace-pre-wrap break-words max-h-[50vh] overflow-y-auto", mono ? "font-mono" : "font-sans")}>
          {content}
        </pre>
      )}
    </div>
  );
}

// Colorized unified-diff view. The worker's changes are presented for review;
// nothing is applied automatically. Copy button grabs the raw patch.
function DiffBlock({ diff, running }: { diff: string; running: boolean }) {
  const [copied, setCopied] = useState(false);
  if (!diff) {
    return (
      <div className="flex items-center gap-2 text-sm text-fg-muted">
        <FileDiff className="size-4" />
        <span>{running ? "Worker still running — no diff yet." : "No changes produced."}</span>
      </div>
    );
  }
  const copy = async () => {
    try { await navigator.clipboard.writeText(diff); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* */ }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <SectionLabel>Diff</SectionLabel>
        <button onClick={copy} className="text-[11px] text-fg-subtle hover:text-fg inline-flex items-center gap-1">
          {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
          {copied ? "copied" : "copy patch"}
        </button>
      </div>
      <pre className="panel text-xs font-mono leading-relaxed overflow-x-auto max-h-[60vh] overflow-y-auto">
        {diff.split("\n").map((line, i) => (
          <div key={i} className={cn("px-3", diffLineClass(line))}>{line || " "}</div>
        ))}
      </pre>
    </div>
  );
}

function diffLineClass(line: string): string {
  if (line.startsWith("@@")) return "text-info bg-info-subtle/40";
  if (line.startsWith("diff --git") || line.startsWith("index ") || line.startsWith("--- ") || line.startsWith("+++ "))
    return "text-fg-subtle";
  if (line.startsWith("+")) return "text-success bg-success-subtle/30";
  if (line.startsWith("-")) return "text-danger bg-danger-subtle/30";
  return "text-fg-muted";
}
